import React, { useEffect, useState, useRef } from 'react'
import File from '../../assets/img/file@2x.png'
import { FileInputContainer } from '../../Containers/PasserCommande/PasserCommande.styles'
import SimulateWaypoints from '../PointsPassages/SimulateWaypoints'
import { simulateWaypoints } from '../../api/commande'
import { Route, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { useSelector } from 'react-redux'
import { uploadFile, deleteMedia } from '../../api/upload'
import { useTranslation } from 'react-i18next'

function UploadRouteLetterFile({
	name,
	initialFiles,
	initialLettreRoute,
	maxFileSize = 10 * 1024 * 1024,
	depotAddress,
	taux_chargement,
	total_kilometrage,
	total_horaire,
	onChange,
	simulation,
	onSimulationComplete,
}) {
	const [file, setFile] = useState(initialFiles || null)
	const acceptedTypes = [
		'application/pdf',
		'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		'application/vnd.ms-excel',
		'text/csv',
		'application/csv',
	]
	const acceptString = '.pdf,.xls,.xlsx,.csv'
	const [loading, setLoading] = useState(false)
	const [showSimulationModal, setShowSimulationModal] = useState(false)
	const [simulationData, setSimulationData] = useState(simulation || null)
	const token = useSelector((state) => state.auth.token)
	const fileInputRef = useRef(null)
	const [simulationDeps, setSimulationDeps] = useState({
		depotAddress,
		taux_chargement,
		total_kilometrage,
		total_horaire,
	})

	// Mettre à jour le fichier et la simulation quand les props changent
	useEffect(() => {
		if (initialFiles !== undefined) {
			setFile(initialFiles)
		}
		if (simulation !== undefined) {
			setSimulationData(simulation)
		}
	}, [initialFiles, simulation])

	// Vérifie si toutes les données requises sont remplies
	const checkRequiredData = (deps) => {
		const requiredFields = [
			deps.depotAddress,
			deps.taux_chargement,
			deps.total_kilometrage,
			deps.total_horaire,
		]
		return requiredFields.every(
			(field) => field !== undefined && field !== null && field !== ''
		)
	}

	// Effectue la simulation avec les paramètres actuels
	const recalculateSimulation = async (fileToProcess, deps) => {
		if (!fileToProcess || !checkRequiredData(deps)) return

		try {
			setLoading(true)
			const simulationData = await simulateWaypoints(
				{
					depotAddress: deps.depotAddress,
					taux_chargement: deps.taux_chargement,
					total_kilometrage: deps.total_kilometrage,
					total_horaire: deps.total_horaire,
				},
				fileToProcess,
				token
			)
			if (simulationData.status) {
				setSimulationData(simulationData)
				setShowSimulationModal(true)
				if (onSimulationComplete) {
					onSimulationComplete(simulationData)
				}
			} else {
				throw new Error(
					simulationData.message || 'Échec de la simulation'
				)
			}
		} catch (error) {
			toast.error(
				error.response?.data?.message ||
					error.response?.data?.error ||
					'Une erreur est survenue lors de la simulation de la lettre de route.',
				{
					style: { fontFamily: 'Montserrat' },
				}
			)
		} finally {
			setLoading(false)
		}
	}

	// Mettre à jour les dépendances de simulation quand les props changent
	useEffect(() => {
		const newDeps = {
			depotAddress: depotAddress || '',
			taux_chargement: taux_chargement || 0,
			total_kilometrage: total_kilometrage || 0,
			total_horaire: total_horaire || 0,
		}

		if (JSON.stringify(newDeps) !== JSON.stringify(simulationDeps)) {
			setSimulationDeps(newDeps)

			// Recalculer la simulation si un fichier est déjà présent
			if (file) {
				recalculateSimulation(file, newDeps)
			}
		}
	}, [depotAddress, taux_chargement, total_kilometrage, total_horaire])

	// Gère le changement de fichier
	const handleFileChange = async (e) => {
		try {
			const selectedFile = e.target.files[0]
			if (!selectedFile) return

			// Validation du fichier
			if (selectedFile.size > maxFileSize) {
				toast.error('Le fichier est trop volumineux (max 10MB)', {
					style: { fontFamily: 'Montserrat' },
				})
				return
			}

			if (!acceptedTypes.includes(selectedFile.type)) {
				toast.error('Seuls les fichiers PDF ou Excel sont acceptés', {
					style: { fontFamily: 'Montserrat' },
				})
				return
			}

			if (!checkRequiredData(simulationDeps)) {
				toast.error(
					'Veuillez remplir toutes les informations de la mission',
					{
						style: { fontFamily: 'Montserrat' },
					}
				)
				return
			}
			// Supprimer l'ancien fichier s'il existe

			if (initialLettreRoute) {
				await deleteMedia(initialLettreRoute)
			}

			const uploadResponse = await uploadFile(selectedFile)
			// Notifier le parent avec les deux valeurs
			if (onChange) {
				onChange({
					id: uploadResponse.data.id,
					file: selectedFile,
				})
			}
			setFile(selectedFile)
			recalculateSimulation(selectedFile, simulationDeps)
		} catch (error) {
			toast.error('Erreur lors du traitement du fichier')
		}
	}

	// Gère la suppression du fichier

	const handleRemoveFile = async () => {
		try {
			setLoading(true)

			// Supprimer le fichier du serveur si un ID existe
			if (initialLettreRoute) {
				await deleteMedia(initialLettreRoute)
			}

			// Réinitialiser les états
			setFile(null)
			setSimulationData(null)
			if (fileInputRef.current) fileInputRef.current.value = ''

			// Notifier le parent
			if (onChange) {
				onChange({
					id: undefined,
					file: undefined,
				})
			}

			if (onSimulationComplete) {
				onSimulationComplete(null)
			}
		} catch (error) {
			console.error('Erreur lors de la suppression du fichier:', error)
			toast.error('Erreur lors de la suppression du fichier')
		} finally {
			setLoading(false)
		}
	}
    const {t} = useTranslation()
	const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

    useEffect(() => {
        const checkScreen = () => {
            const width = window.innerWidth;

            setIsMobile(width < 768);

        };
        checkScreen();
        window.addEventListener('resize', checkScreen);

        return () => window.removeEventListener('resize', checkScreen);
    }, []);
	return (
		<div
			style={{
				display: 'flex',
				flexDirection : isMobile && file ? 'column' : 'row',
				alignItems: 'center',
				justifyContent: 'space-between', 
			}}
		>
			<div
				style={{
					display: 'flex',
					flexDirection : isMobile && file ? 'column' : 'row',
					gap : isMobile && file ? '1rem' : 0,
					flex: 1,
					borderRadius: '0.75rem',
					background: 'white',
					alignItems: 'center',
					justifyContent: 'space-between',
					marginBottom: '1.5rem',
					position: 'relative',
					opacity: 1,
					border: `2px dashed  #dee2e6`,
					transition: 'all 0.3s ease',
					padding: '0.75rem',
					'&:hover': {
						borderColor: '#2a9fd6',
						boxShadow: '0 2px 8px rgba(81, 199, 242, 0.15)',
					},
				}}
			>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: '1rem',
						flex: 1,
					}}
				>
					{file ? (
						<>
							<div
								style={{
									background: '#e3f8ff',
									borderRadius: '8px',
									padding: '0.5rem',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}
							>
								<img
									src={File}
									style={{
										height: 28,
										width: 28,
										filter: 'invert(56%) sepia(74%) saturate(378%) hue-rotate(161deg) brightness(91%) contrast(93%)',
									}}
									alt='Fichier'
								/>
							</div>
							<div style={{ flex: 1 }}>
								<p
									style={{
										margin: 0,
										fontWeight: 500,
										color: '#212529',
										fontSize: '0.9rem',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
									}}
								>
									{file.name}
								</p>
								<p
									style={{
										margin: 0,
										color: '#868e96',
										fontSize: '0.75rem',
										marginTop: '0.25rem',
									}}
								>
									{Math.round(file.size / 1024)} KB
								</p>
							</div>
						</>
					) : (
						<span
							style={{
								color: '#868e96',
								fontStyle: 'italic',
								fontSize: '0.9rem',
							}}
						>
							{t("no_file_selected")}
						</span>
					)}
				</div>
                <div style={{display :'flex' , flexDirection : 'row'}}>
				<FileInputContainer>
					<input
						type='file'
						id={name}
						name={name}
						className='file'
						accept={acceptString}
						onChange={handleFileChange}
						disabled={loading}
						style={{ cursor: loading ? 'not-allowed' : 'pointer' }}
						ref={fileInputRef}
					/>
					<label
						htmlFor={name}
						style={{
							cursor: loading ? 'not-allowed' : 'pointer',
							backgroundColor: loading ? '#e9ecef' : '#f1f3f5',
							color: loading ? '#adb5bd' : '#212529',
							padding: '0.5rem 1rem',
							borderRadius: '6px',
							fontSize: '0.875rem',
							fontWeight: 500,
							transition: 'all 0.2s ease',
							'&:hover': !loading && {
								backgroundColor: '#e3f8ff',
								color: '#087ea4',
							},
						}}
					>
						{loading ? (
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '0.5rem',
								}}
							>
								<div
									className='loader'
									style={{ width: '16px', height: '16px' }}
								/>
								{t("loading")}...
							</div>
						) : (
							t("select_file")
						)}
					</label>
				</FileInputContainer>
				{file && (
					<button
						onClick={handleRemoveFile}
						style={{
							marginLeft: '1rem',
							background: 'transparent',
							border: 'none',
							color: '#dc2626',
							cursor: 'pointer',
							display: 'flex',
							alignItems: 'center',
						}}
						disabled={loading}
					>
						<Trash2 size={18} />
					</button>
				)}
</div>
				<SimulateWaypoints
					isOpen={showSimulationModal}
					onClose={() => setShowSimulationModal(false)}
					simulation={simulationData}
					setSimulation={setSimulationData}
					onSimulationComplete={(data) => {
						onSimulationComplete(data)
					}}
				/>
			</div>
			{/* Ajout du bouton d'affichage de la simulation */}
			{simulationData && (
				<button
					type='button'
					onClick={() => setShowSimulationModal(true)}
					style={{
						marginLeft: '1rem',
						padding: '0.75rem 1.5rem',
						backgroundColor: '#f0fbff',
						color: '#087ea4',
						border: '2px solid #c5f3ff',
						borderRadius: '8px',
						cursor: 'pointer',
						display: 'flex',
						alignItems: 'center',
						gap: '0.75rem',
						fontSize: '0.875rem',
						fontWeight: 600,
						transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
						boxShadow: '0 2px 4px rgba(8, 126, 164, 0.1)',
						'&:hover': {
							backgroundColor: '#c5f3ff',
							transform: 'translateY(-2px)',
							boxShadow: '0 4px 8px rgba(8, 126, 164, 0.15)',
							borderColor: '#9ae3ff',
						},
						'&:active': {
							transform: 'translateY(0)',
							boxShadow: '0 2px 4px rgba(8, 126, 164, 0.1)',
						},
					}}
				>
					<Route
						size={20}
						style={{
							strokeWidth: '2px',
							color: '#087ea4',
							transition: 'transform 0.3s ease',
						}}
					/>
					<span>{t("view_route")}</span>
				</button>
			)}
		</div>
	)
}

export default UploadRouteLetterFile

